import { useCallback, useMemo, useState, type ChangeEvent } from 'react'
import {
  startToolOperation,
  trackToolRejection,
  type ToolAnalyticsContext,
  type ToolErrorCode,
} from '@saas-pages/shared/public/runtime'
import { Button, Card, CardContent, cn } from '@saas-pages/shared/ui'
import {
  calculateDiscount,
  DISCOUNT_MONEY_DECIMAL_PLACES,
  DISCOUNT_PERCENT_DECIMAL_PLACES,
  MAX_DISCOUNT_INPUT_LENGTH,
  MAX_DISCOUNT_STEPS,
  type DiscountAnalyticsMode,
  type DiscountCalculation,
  type DiscountCalculationError,
  type DiscountField,
  type DiscountInputs,
  type DiscountMode,
} from '@/pages/discount-calculator/discount-calculator'
import { useOnSettled, useSettledValue } from '@/shared/use-settled'
import { discountCalculatorRoute } from '@/shared/utility-registry'

interface ModeOption {
  key: DiscountMode
  title: string
  description: string
}

const MODES: ModeOption[] = [
  {
    key: 'direct',
    title: 'Цена после скидки',
    description: 'Исходная цена и до трёх скидок',
  },
  {
    key: 'reverse',
    title: 'Исходная цена',
    description: 'Итоговая цена и известные скидки',
  },
  {
    key: 'compare',
    title: 'Скидка по ценам',
    description: 'Старая и новая цена',
  },
]

const INITIAL_INPUTS: DiscountInputs = {
  originalPrice: '1000',
  finalPrice: '680',
  discounts: ['20', '15'],
}

export function discountAnalyticsContext(mode: DiscountAnalyticsMode): ToolAnalyticsContext {
  return {
    app: 'calcdeck',
    domain: 'calcdeck.ru',
    route: discountCalculatorRoute.path,
    utility: `${discountCalculatorRoute.analyticsKey}.${mode}`,
    processingMode: 'local',
  }
}

export function discountAnalyticsError(error: DiscountCalculationError): ToolErrorCode {
  if (error === 'number_too_long') return 'size_limit'
  if (error === 'irreversible_discount') return 'unsupported_input'
  return 'invalid_input'
}

function reportDiscountCalculation({ calculation, mode, discountCount }: {
  calculation: DiscountCalculation
  mode: DiscountMode
  discountCount: number
}): void {
  if (calculation.status === 'success') {
    startToolOperation(discountAnalyticsContext(calculation.result.analyticsMode)).success(0)
    return
  }
  const analyticsMode: DiscountAnalyticsMode = mode === 'direct'
    ? discountCount === 1 ? 'direct' : 'stacked'
    : mode
  trackToolRejection(discountAnalyticsContext(analyticsMode), discountAnalyticsError(calculation.error))
}

export function DiscountCalculatorPage() {
  const [mode, setMode] = useState<DiscountMode>('direct')
  const [inputs, setInputs] = useState<DiscountInputs>(INITIAL_INPUTS)
  const calculation = useMemo(() => calculateDiscount(mode, inputs), [inputs, mode])

  const report = useMemo(
    () => ({ calculation, mode, discountCount: inputs.discounts.length }),
    [calculation, inputs.discounts.length, mode],
  )
  useOnSettled(report, reportDiscountCalculation)

  const updatePrice = useCallback((field: 'originalPrice' | 'finalPrice') => (
    event: ChangeEvent<HTMLInputElement>,
  ) => {
    setInputs((current) => ({ ...current, [field]: event.target.value }))
  }, [])

  const updateDiscount = useCallback((index: number) => (
    event: ChangeEvent<HTMLInputElement>,
  ) => {
    setInputs((current) => ({
      ...current,
      discounts: current.discounts.map((value, currentIndex) => (
        currentIndex === index ? event.target.value : value
      )),
    }))
  }, [])

  const setDiscountCount = useCallback((count: number) => {
    setInputs((current) => ({
      ...current,
      discounts: Array.from(
        { length: count },
        (_, index) => current.discounts[index] ?? '',
      ),
    }))
  }, [])

  const reset = useCallback(() => {
    setMode('direct')
    setInputs(INITIAL_INPUTS)
  }, [])

  const liveError = calculation.status === 'error' ? calculation : null
  const settledError = useSettledValue(liveError)
  const error = liveError === null ? null : settledError
  const result = calculation.status === 'success' ? calculation.result : null
  const activeMode = MODES.find((item) => item.key === mode) ?? MODES[0]!

  return (
    <div className="ym-hide-content mx-auto max-w-6xl">
      <section className="overflow-hidden rounded-lg border bg-card text-card-foreground shadow">
        <div aria-hidden="true" className="h-1.5 bg-gradient-to-r from-blue-800 via-blue-500 to-cyan-400" />
        <div className="grid lg:grid-cols-[minmax(0,1fr)_minmax(320px,0.88fr)]">
          <div className="min-w-0 space-y-3 border-b p-4 sm:space-y-5 sm:p-6 lg:border-b-0 lg:border-r">
            <div className="grid grid-cols-3 gap-1 sm:gap-2" role="tablist" aria-label="Режим расчёта скидки">
              {MODES.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  role="tab"
                  aria-selected={mode === item.key}
                  onClick={() => setMode(item.key)}
                  className={cn(
                    'min-h-11 min-w-0 rounded-md border px-1.5 py-1.5 text-center text-sm leading-tight transition-colors sm:p-3 sm:text-left',
                    mode === item.key
                      ? 'border-primary bg-primary text-primary-foreground shadow-sm'
                      : 'bg-background hover:border-primary/40 hover:bg-accent/60',
                  )}
                >
                  <span className="block font-semibold">{item.title}</span>
                  <span className={cn(
                    'mt-1 hidden text-xs leading-5 sm:block',
                    mode === item.key ? 'text-primary-foreground/80' : 'text-muted-foreground',
                  )}
                  >
                    {item.description}
                  </span>
                </button>
              ))}
            </div>

            <div className="sm:rounded-md sm:border sm:bg-background/70 sm:p-4 sm:shadow-inner">
              <div className="mb-4 hidden sm:block">
                <h2 className="text-base font-semibold tracking-normal">{activeMode.title}</h2>
                <p className="mt-1 text-xs leading-5 text-muted-foreground">
                  Можно использовать точку или запятую как десятичный разделитель.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3 sm:gap-4">
                {mode !== 'reverse' && (
                  <NumberField
                    id="discount-original-price"
                    label={mode === 'compare' ? 'Старая цена' : 'Исходная цена'}
                    value={inputs.originalPrice}
                    placeholder="1000"
                    invalid={Boolean(error?.invalidFields.includes('originalPrice'))}
                    onChange={updatePrice('originalPrice')}
                  />
                )}
                {mode !== 'direct' && (
                  <NumberField
                    id="discount-final-price"
                    label={mode === 'compare' ? 'Новая цена' : 'Итоговая цена'}
                    value={inputs.finalPrice}
                    placeholder="680"
                    invalid={Boolean(error?.invalidFields.includes('finalPrice'))}
                    onChange={updatePrice('finalPrice')}
                  />
                )}
              </div>

              {mode !== 'compare' && (
                <div className="mt-4">
                  <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                    <span className="text-sm font-medium">Последовательные скидки</span>
                    <div className="inline-flex rounded-md border bg-card p-1" aria-label="Количество скидок">
                      {Array.from({ length: MAX_DISCOUNT_STEPS }, (_, index) => index + 1).map((count) => (
                        <button
                          key={count}
                          type="button"
                          aria-pressed={inputs.discounts.length === count}
                          onClick={() => setDiscountCount(count)}
                          className={cn(
                            'rounded px-3 py-1 text-sm transition-colors',
                            inputs.discounts.length === count
                              ? 'bg-primary text-primary-foreground'
                              : 'text-muted-foreground hover:bg-accent hover:text-foreground',
                          )}
                        >
                          {count}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-2 sm:gap-4">
                    {inputs.discounts.map((discount, index) => (
                      <NumberField
                        key={index}
                        id={`discount-step-${index}`}
                        label={`Скидка ${index + 1}`}
                        value={discount}
                        placeholder={index === 0 ? '20' : '15'}
                        suffix="%"
                        invalid={Boolean(error?.invalidFields.includes(`discount-${index}` as DiscountField))}
                        onChange={updateDiscount(index)}
                      />
                    ))}
                  </div>
                </div>
              )}

              {error && (
                <div
                  id="discount-input-error"
                  role="alert"
                  className="mt-4 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
                >
                  {errorText(error.error)}
                </div>
              )}
            </div>
          </div>

          <div aria-live="polite" className="bg-background/55 p-4 sm:p-6 lg:min-h-[390px]">
            <div className="text-sm font-medium text-muted-foreground">Результат</div>
            {result ? (
              <>
                <div className="mt-2 grid grid-cols-2 gap-2 sm:mt-4 sm:gap-3">
                  <ResultValue label="Итоговая цена" value={result.finalPrice.text} suffix="ден. ед." rounded={result.finalPrice.rounded} />
                  <ResultValue label="Исходная цена" value={result.originalPrice.text} suffix="ден. ед." rounded={result.originalPrice.rounded} />
                  <ResultValue label="Экономия" value={result.savings.text} suffix="ден. ед." rounded={result.savings.rounded} />
                  <ResultValue label="Эффективная скидка" value={result.effectiveDiscount.text} suffix="%" rounded={result.effectiveDiscount.rounded} />
                </div>
                <div className="mt-4 rounded-md border bg-card p-4">
                  <div className="text-xs font-medium uppercase text-muted-foreground">Формула</div>
                  <div className="mt-2 font-mono text-sm leading-6">{result.formula}</div>
                  <div className="mt-2 break-words text-xs leading-5 text-muted-foreground">{result.substitution}</div>
                </div>
                <div className="mt-3">
                  <Button variant="ghost" onClick={reset}>Сбросить</Button>
                </div>
              </>
            ) : (
              <p className="mt-2 text-sm leading-6 text-muted-foreground">
                {error ? 'Проверьте исходные данные.' : 'Введите значения: цены, экономия и формула появятся здесь сразу.'}
              </p>
            )}
          </div>
        </div>
      </section>

      <section className="mt-8 grid gap-4 md:grid-cols-3">
        <InfoCard
          title="Почему 20% + 15% ≠ 35%"
          text="Вторая скидка применяется к уже уменьшенной цене: 100 × 0,8 × 0,85 = 68. Общая экономия равна 32%, а не 35%."
        />
        <InfoCard
          title="Точные десятичные числа"
          text="Расчёт использует точные десятичные дроби без ошибок двоичного floating point."
        />
        <InfoCard
          title="Политика округления"
          text={`Деньги показываются максимум с ${DISCOUNT_MONEY_DECIMAL_PLACES} знаками, проценты — с ${DISCOUNT_PERCENT_DECIMAL_PLACES}. Половины округляются от нуля; вычисления не округляются между шагами.`}
        />
      </section>
    </div>
  )
}

interface NumberFieldProps {
  id: string
  label: string
  value: string
  placeholder: string
  suffix?: string
  invalid: boolean
  onChange: (event: ChangeEvent<HTMLInputElement>) => void
}

function NumberField({
  id,
  label,
  value,
  placeholder,
  suffix,
  invalid,
  onChange,
}: NumberFieldProps) {
  return (
    <label htmlFor={id} className="block">
      <span className="mb-2 block text-sm font-medium">{label}</span>
      <span className="relative block">
        <input
          id={id}
          inputMode="decimal"
          value={value}
          placeholder={placeholder}
          maxLength={MAX_DISCOUNT_INPUT_LENGTH + 1}
          aria-invalid={invalid || undefined}
          aria-describedby={invalid ? 'discount-input-error' : undefined}
          onChange={onChange}
          className={cn(
            'h-11 w-full rounded-md border bg-card px-3 text-base outline-none transition-colors',
            'ym-disable-keys',
            'focus:border-primary focus:ring-2 focus:ring-ring/20',
            suffix && 'pr-10',
            invalid && 'border-destructive focus:border-destructive focus:ring-destructive/20',
          )}
        />
        {suffix && (
          <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm text-muted-foreground">
            {suffix}
          </span>
        )}
      </span>
    </label>
  )
}

interface ResultValueProps {
  label: string
  value: string
  suffix: string
  rounded: boolean
}

function ResultValue({ label, value, suffix, rounded }: ResultValueProps) {
  return (
    <div className="min-w-0 rounded-md border bg-card p-3 shadow-sm">
      <div className="text-xs text-muted-foreground">{label}</div>
      <output className="mt-1 block break-all font-mono text-xl font-semibold tabular-nums">
        {value} {suffix}
      </output>
      {rounded && <div className="mt-1 text-xs text-muted-foreground">Округлено для отображения</div>}
    </div>
  )
}

interface InfoCardProps {
  title: string
  text: string
}

function InfoCard({ title, text }: InfoCardProps) {
  return (
    <Card>
      <CardContent className="pt-6">
        <h3 className="font-semibold">{title}</h3>
        <p className="mt-2 text-sm leading-6 text-muted-foreground">{text}</p>
      </CardContent>
    </Card>
  )
}

function errorText(error: DiscountCalculationError): string {
  if (error === 'empty_input') return 'Заполните все поля текущего режима.'
  if (error === 'invalid_number') return 'Введите обычное десятичное число без пробелов и экспоненты.'
  if (error === 'number_too_long') return `Каждое число должно быть не длиннее ${MAX_DISCOUNT_INPUT_LENGTH} символов.`
  if (error === 'negative_price') return 'Цена не может быть отрицательной.'
  if (error === 'discount_out_of_range') return 'Скидка должна быть от 0% до 100%.'
  if (error === 'zero_original_price') return 'Для нулевой старой цены нельзя вычислить процент скидки.'
  if (error === 'new_price_above_old') return 'Новая цена не должна быть выше старой цены.'
  return 'Скидку 100% нельзя обратить: по нулевой итоговой цене исходная цена не определяется.'
}
